<div class="cable-dialog">
  <!-- Article header -->
  <div class="article-header">
    <div class="article-header__label">{{ 'components.cableDialog.article' | translate }}</div>
    <div class="article-header__value">{{ model.material?.materialNumber }} - {{ model.material?.description }}</div>
  </div>

  <!-- Company -->
  <div class="company-row">
    <div class="company-row__label">{{ 'components.cableDialog.company' | translate }}</div>
    <div class="company-row__value">
      <span>{{ hasCompany() ? getCompanyName() : ('components.cableDialog.notSelected' | translate) }}</span>
      <button
        mat-icon-button
        class="company-edit"
        (click)="editCompany()"
        [attr.aria-label]="'components.cableDialog.editCompany' | translate"
      >
        <em class="material-icons">edit</em>
      </button>
    </div>
  </div>

  <!-- Location input -->
  <form [formGroup]="cableForm">
    @if (isArticleMissing()) {
      <div class="location-warning">
        {{ 'components.cableDialog.articleMissing' | translate }}
      </div>
    } @else if (locationsLoading) {
      <div class="location-loading">
        <mat-spinner diameter="16"></mat-spinner>
        <span>{{ 'components.cableDialog.loadingLocations' | translate }}</span>
      </div>
    } @else if (hasLocations()) {
      <mat-form-field appearance="fill" class="location-field">
        <mat-label>{{ 'components.cableDialog.location' | translate }}</mat-label>
        <mat-select formControlName="locationExternalId">
          @for (location of locations; track location.id) {
            <mat-option [value]="location.id">
              {{ location.number ? location.number + ' — ' + location.name : location.name }}
              @if (location.quantity != null) {
                <span class="location-qty">{{
                  'components.cableDialog.quantity' | translate: { quantity: location.quantity }
                }}</span>
              }
            </mat-option>
          }
        </mat-select>
        @if (cableForm.get('locationExternalId').hasError('required')) {
          <mat-error>{{ 'components.cableDialog.locationRequired' | translate }}</mat-error>
        }
      </mat-form-field>
    } @else {
      <div class="location-warning">
        {{ 'workOrderLines.cableNoLocationsFound' | translate }}
      </div>
    }
  </form>

  <!-- Planned vs Installed -->
  <div class="progress">
    <div class="planned">
      <div class="label">{{ 'components.cableDialog.plannedLength' | translate }}</div>
      <div class="planned-length">
        {{ model.plannedLength }} <span class="unit">{{ model.unit }}</span>
      </div>
    </div>

    <div class="installed">
      <form [formGroup]="cableForm">
        <div class="label">{{ 'components.cableDialog.pulledFromDrum' | translate }}</div>
        <div class="length-placeholder">
          <mat-form-field class="whole length-form-field">
            <input
              matInput
              id="whole"
              type="tel"
              formControlName="whole"
              autocomplete="off"
              #wholeInput
              (click)="wholeInput.select()"
            />
          </mat-form-field>
          <span>,</span>
          <mat-form-field class="decimal length-form-field">
            <input
              matInput
              id="decimal"
              type="tel"
              formControlName="decimal"
              autocomplete="off"
              #decimalInput
              (click)="decimalInput.select()"
              [maxLength]="2"
            />
          </mat-form-field>
          <span class="unit-label">{{ model.unit }}</span>
        </div>
      </form>
    </div>
  </div>

  <!-- Validation errors -->
  <div class="error-placeholder">
    @if (cableForm.get('whole').hasError('min')) {
      <mat-error>
        <label for="whole">{{ 'components.cableDialog.wholeNegative' | translate }}</label>
      </mat-error>
    }
    @if (cableForm.get('whole').hasError('pattern')) {
      <mat-error>
        <label for="whole">{{ 'components.cableDialog.wholeNotNumber' | translate }}</label>
      </mat-error>
    }
    @if (cableForm.get('whole').hasError('required')) {
      <mat-error>
        <label for="whole">{{ 'components.cableDialog.wholeRequired' | translate }}</label>
      </mat-error>
    }
    @if (cableForm.get('decimal').hasError('min')) {
      <mat-error>
        <label for="decimal">{{ 'components.cableDialog.decimalNegative' | translate }}</label>
      </mat-error>
    }
    @if (cableForm.get('decimal').hasError('max')) {
      <mat-error>
        <label for="decimal">{{ 'components.cableDialog.decimalMax' | translate }}</label>
      </mat-error>
    }
    @if (cableForm.get('decimal').hasError('pattern')) {
      <mat-error>
        <label for="decimal">{{ 'components.cableDialog.decimalNotNumber' | translate }}</label>
      </mat-error>
    }
    @if (exceedsAvailableQuantity()) {
      <mat-error>
        {{ 'workOrderLines.cablePulledLengthExceedsAvailable' | translate }}
      </mat-error>
    }
  </div>

  <!-- Quick actions -->
  <div class="quick-actions">
    @if (showStepButton(-100)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-negative" (click)="decreaseValueBy(100)">-100</button>
      </div>
    }
    @if (showStepButton(-50)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-negative" (click)="decreaseValueBy(50)">-50</button>
      </div>
    }
    @if (showStepButton(-10)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-negative" (click)="decreaseValueBy(10)">-10</button>
      </div>
    }
    @if (showStepButton(-1)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-negative" (click)="decreaseValueBy(1)">-1</button>
      </div>
    }
    <div class="button-placeholder">
      <button mat-flat-button (click)="setValuesToPlanned()">
        {{ 'components.cableDialog.asPlanned' | translate }}
      </button>
    </div>
    @if (showStepButton(1)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-positive" (click)="increaseValueBy(1)">+1</button>
      </div>
    }
    @if (showStepButton(10)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-positive" (click)="increaseValueBy(10)">+10</button>
      </div>
    }
    @if (showStepButton(50)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-positive" (click)="increaseValueBy(50)">+50</button>
      </div>
    }
    @if (showStepButton(100)) {
      <div class="button-placeholder">
        <button mat-flat-button class="btn-positive" (click)="increaseValueBy(100)">+100</button>
      </div>
    }
  </div>

  <!-- Dialog actions -->
  <div class="dialog-actions">
    <div class="dialog-actions-column">
      <button color="warn" mat-flat-button class="cancel" (click)="cancel()">
        {{ 'components.cableDialog.cancel' | translate }}
      </button>
    </div>
    <div class="dialog-actions-column dialog-actions-column-right">
      <button color="primary" mat-flat-button class="confirm" [disabled]="canSubmit()" (click)="confirm()">
        {{ 'components.cableDialog.confirm' | translate }}
      </button>
    </div>
  </div>
</div>
